<template>
  <div
    id="app-scrollcontainer"
    ref="scrollContainer"
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
    v-test="'app-scrollcontainer'"
  >
    <div
      ref="contentWrap"
      :class="$style.contentWrap"
    >
      <div :class="$style.header">
        <slot name="header" />
      </div>
      <div :class="$style.page">
        <slot name="page" />
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useResizeObserver } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { usePageScroll } from '@/helpers/scroll';
import { usePageLayoutStore } from '@/stores/page-layout';
import { device } from '@/user-context';

const scrollContainer = ref();
const contentWrap = ref();
const scrollDisabled = computed(() => {
  const { isModalOpen } = usePageLayoutStore();
  return isModalOpen;
});

const pageLayoutStore = usePageLayoutStore();
const { screenSize, isNavExpanded, isScrollbarVisible } =
  storeToRefs(pageLayoutStore);

usePageScroll(screenSize.value === 's' ? undefined : scrollContainer, {
  disabled: scrollDisabled,
});

/*
  This code provides a way of removing the "flickering" or "jumping" of content when content changes.
  This is due to the scrollbar appearing or disappearing based on the length of the content, pushing the content to the left.
  To make this work it needs 3 elements.
  - The "outer" element is the one containing the scrollbar
  - The "middle" element is the content if which the width changes based on the scrollbar. This is the element that is observed by the resizeobserver.
  - The "inner" element is the one that gets a padding based on the width of the scrollbar.
  We can't put the padding on the middle element, because that would trigger the resizeobserver again, resulting in an infinite loop.

  This code doesn't need to run on touch devices, as there is no scrollbar there.

  This does not work on old browsers, but that's fine.
  https://caniuse.com/resizeobserver
*/

const shouldObserve = computed(() => {
  return (
    window.ResizeObserver &&
    !device.touch &&
    !!scrollContainer.value &&
    !!contentWrap.value
  );
});

// Suppress resize observer callbacks during nav expansion animation to prevent
// rapid toggling of isScrollbarVisible (which causes header padding flicker).
const isNavAnimating = ref(false);

useResizeObserver(contentWrap, (entries) => {
  if (!shouldObserve.value || isNavAnimating.value) {
    return;
  }

  const scrollElement = scrollContainer.value;
  const entry = entries[0];

  const checkScrollbar = (width: number) => {
    isScrollbarVisible.value = width !== scrollElement.offsetWidth;
  };

  if (entry.contentBoxSize && entry.contentBoxSize.length > 0) {
    checkScrollbar(entry.contentBoxSize[0].inlineSize);
  } else {
    checkScrollbar(entry.contentRect.width);
  }
});

watch(isNavExpanded, () => {
  // Suppress resize callbacks during the nav animation to avoid flickering,
  // then do a single check once the animation completes (matching old behavior
  // of unobserve/re-observe).
  isNavAnimating.value = true;

  setTimeout(() => {
    isNavAnimating.value = false;

    // Perform the final scrollbar recalculation that may have been suppressed
    // during the animation window.
    if (shouldObserve.value) {
      const scrollElement = scrollContainer.value;
      const contentWrapElement = contentWrap.value;
      isScrollbarVisible.value =
        contentWrapElement.offsetWidth !== scrollElement.offsetWidth;
    }
  }, 500);
});
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
  position: relative;

  &:not(.smallScreen) {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.contentWrap {
  height: 100%;
}

.page {
  .base:not(.smallScreen) & {
    height: calc(100% - #{$header-height});
  }

  .base.smallScreen & {
    height: 100%;
  }
}
</style>
